import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CoverageMetricTableCell } from '@/features/codeCoverage/pages/CoverageMetricTableCell';
import {
  resolveCoverageMetricThresholdColor,
  type CoverageMetricThresholdKey,
} from '@/features/codeCoverage/pages/coverageMetricThresholdColors';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { TableBreakdownLine } from '@/features/shared/components/tableSystem/TableBreakdownLine';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import {
  TABLE_GRID_HEADER_HEIGHT,
  TABLE_ROW_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import {
  STATUS_META_TIMESTAMP_CONTENT_SX,
  STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX,
  TableBreakdownStack,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { formatEntityTagsLabel } from '@/features/shared/tags';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { TableNameCell } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import {
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridSortModel,
} from '@/ui/primitives';
import { EventOutlinedIcon, FolderOutlinedIcon } from '@/ui/primitives/icons';
import { buildDirectoryPathHref, type CoverageDirectoryRow } from './coverageEntityRows';
import { useCodeDirectoryTableRowActions } from './useCodeDirectoryTableRowActions';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { MetricThreshold, QueryResult } from '@flowqa/shared';

type CoverageDirectoriesTableProps = {
  directories: CoverageDirectoryRow[];
  queryKey: QueryKey;
  projectId?: string | null;
  initialSortModel?: GridSortModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  loading?: boolean;
  onColumnVisibilityModelChange?: (nextModel: GridColumnVisibilityModel) => void;
};

const formatPercent = (value: number | null | undefined): string =>
  value === null || value === undefined ? '-' : `${value.toFixed(2)}%`;
const formatCountPair = (
  covered: number | null | undefined,
  total: number | null | undefined,
): string => {
  if (covered === null || covered === undefined || total === null || total === undefined) {
    return '-';
  }
  return `${String(covered)} / ${String(total)}`;
};

const buildMetricColumn = (
  field: string,
  headerName: string,
  thresholdKey: CoverageMetricThresholdKey,
  resolvePercent: (row: CoverageDirectoryRow) => number | null | undefined,
  resolveCovered: (row: CoverageDirectoryRow) => number | null | undefined,
  resolveTotal: (row: CoverageDirectoryRow) => number | null | undefined,
  resolveThreshold: (metricKey: CoverageMetricThresholdKey) => MetricThreshold,
): GridColDef<CoverageDirectoryRow> => ({
  field,
  headerName,
  flex: 0.55,
  sortable: false,
  filterable: false,
  align: 'left',
  headerAlign: 'left',
  cellClassName: 'table-metric-cell',
  renderCell: (params) => (
    <TableCellContainer gap="none">
      <CoverageMetricTableCell
        primary={formatPercent(resolvePercent(params.row))}
        secondary={formatCountPair(resolveCovered(params.row), resolveTotal(params.row))}
        primaryColor={resolveCoverageMetricThresholdColor(
          resolvePercent(params.row),
          resolveThreshold(thresholdKey),
        )}
      />
    </TableCellContainer>
  ),
});

const compareStrings = (
  leftValue: string | null | undefined,
  rightValue: string | null | undefined,
): number => (leftValue ?? '').localeCompare(rightValue ?? '', undefined, { sensitivity: 'base' });

const compareNumbers = (
  leftValue: number | null | undefined,
  rightValue: number | null | undefined,
): number => (leftValue ?? Number.NEGATIVE_INFINITY) - (rightValue ?? Number.NEGATIVE_INFINITY);

const compareDates = (
  leftValue: Date | string | null | undefined,
  rightValue: Date | string | null | undefined,
): number => new Date(leftValue ?? 0).getTime() - new Date(rightValue ?? 0).getTime();

const sortDirectories = (
  directories: CoverageDirectoryRow[],
  input: GridQueryInput,
): CoverageDirectoryRow[] => {
  const sort = input.sorts[0] ?? { field: 'normalizedPath', direction: 'asc' as const };
  const factor = sort.direction === 'asc' ? 1 : -1;

  return [...directories].sort((left, right) => {
    let result = 0;
    switch (sort.field) {
      case 'linesPct':
        result = compareNumbers(left.linesPct, right.linesPct);
        break;
      case 'functionsPct':
        result = compareNumbers(left.functionsPct, right.functionsPct);
        break;
      case 'branchesPct':
        result = compareNumbers(left.branchesPct, right.branchesPct);
        break;
      case 'statementsPct':
        result = compareNumbers(left.statementsPct, right.statementsPct);
        break;
      case 'latestCapturedAt':
        result = compareDates(left.latestCapturedAt, right.latestCapturedAt);
        break;
      case 'fileCount':
        result = compareNumbers(left.fileCount, right.fileCount);
        break;
      case 'normalizedPath':
      default:
        result = compareStrings(left.normalizedPath, right.normalizedPath);
        break;
    }

    return result * factor;
  });
};

export const CoverageDirectoriesTable = ({
  directories,
  queryKey,
  projectId = null,
  initialSortModel,
  columnVisibilityModel,
  loading = false,
  onColumnVisibilityModelChange,
}: CoverageDirectoriesTableProps): ReactElement => {
  const { thresholds } = useProjectMetricThresholds(projectId);
  const [editTarget, setEditTarget] = useState<CoverageDirectoryRow | null>(null);
  const resolveThreshold = useCallback(
    (metricKey: CoverageMetricThresholdKey) => thresholds.directories[metricKey],
    [thresholds.directories],
  );
  const {
    deleteLoading,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useCodeDirectoryTableRowActions({ selectedProjectId: projectId });
  const columns = useMemo<Array<GridColDef<CoverageDirectoryRow>>>(
    () => [
      {
        field: 'directory',
        headerName: 'Directory',
        flex: 1,
        sortable: false,
        cellClassName: 'table-name-cell',
        renderCell: (params) => (
          <TableCellContainer gap="none">
            <TableNameCell
              primary={{
                label: params.row.name,
                href: buildDirectoryPathHref(params.row.id),
                sx: { fontFamily: (theme) => theme.typography.mono.fontFamily },
              }}
              secondary={{
                label: params.row.normalizedPath,
                sx: { fontFamily: (theme) => theme.typography.mono.fontFamily },
              }}
            />
          </TableCellContainer>
        ),
      },
      buildMetricColumn(
        'lineCoverage',
        'Line',
        'line',
        (row) => row.linesPct,
        (row) => row.linesCovered,
        (row) => row.linesTotal,
        resolveThreshold,
      ),
      buildMetricColumn(
        'functionCoverage',
        'Function',
        'function',
        (row) => row.functionsPct,
        (row) => row.functionsCovered,
        (row) => row.functionsTotal,
        resolveThreshold,
      ),
      buildMetricColumn(
        'branchCoverage',
        'Branch',
        'branch',
        (row) => row.branchesPct,
        (row) => row.branchesCovered,
        (row) => row.branchesTotal,
        resolveThreshold,
      ),
      buildMetricColumn(
        'statementCoverage',
        'Statement',
        'statement',
        (row) => row.statementsPct,
        (row) => row.statementsCovered,
        (row) => row.statementsTotal,
        resolveThreshold,
      ),
      {
        field: 'details',
        headerName: getDetailLabel('details'),
        flex: 0.8,
        sortable: false,
        filterable: false,
        cellClassName: 'table-meta-details-cell table-meta-details-four-line-cell',
        renderCell: (params) => {
          const capturedAt = params.row.latestCapturedAt;
          const capturedLabel = capturedAt ? formatRelativeTime(capturedAt) : '-';
          const capturedTitle = capturedAt ? formatDate(capturedAt, { includeTime: true }) : '-';

          return (
            <TableCellContainer gap="none">
              <TableBreakdownStack>
                <TableBreakdownLine
                  rowTitle="Path"
                  icon={<FolderOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
                  content={params.row.normalizedPath}
                  disableLinks
                  contentSx={{ fontFamily: (theme) => theme.typography.mono.fontFamily }}
                />
                <TableBreakdownLine
                  rowTitle="Files"
                  icon={<FolderOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
                  content={`${String(params.row.fileCount)} files`}
                  disableLinks
                />
                <TableBreakdownLine
                  rowTitle="Latest"
                  icon={<EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
                  content={capturedLabel}
                  valueTitle={capturedTitle}
                  disableLinks
                  contentSx={STATUS_META_TIMESTAMP_CONTENT_SX}
                  valueWrapperSx={STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX}
                />
                <TableBreakdownLine
                  rowTitle="Tags"
                  icon={<FolderOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
                  content={formatEntityTagsLabel(params.row.tags)}
                  disableLinks
                />
              </TableBreakdownStack>
            </TableCellContainer>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        flex: 0.1,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <TableActionsCell
            rowName={params.row.name}
            editActionLabel={getEntityActionLabel('edit', 'Code Directory')}
            onEdit={() => {
              setEditTarget(params.row);
            }}
            disableEdit={updateRowTagsColorPendingId === params.row.id}
            showEditTagsAction={false}
            deleteActionLabel={getEntityActionLabel('delete', 'Code Directory')}
            onDelete={() => {
              onDeleteRow(params.row);
            }}
          />
        ),
      },
    ],
    [onDeleteRow, resolveThreshold, updateRowTagsColorPendingId],
  );

  const queryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<CoverageDirectoryRow>> => {
      const sortedDirectories = sortDirectories(directories, input);
      const page = Math.max(input.page, 1);
      const pageSize = input.pageSize;
      const startIndex = (page - 1) * pageSize;
      return await Promise.resolve({
        items: sortedDirectories.slice(startIndex, startIndex + pageSize),
        total: sortedDirectories.length,
        page,
        pageSize,
      });
    },
    [directories],
  );

  return (
    <>
      <ServerDataTable<CoverageDirectoryRow>
        queryPath="coverage-directories/local-table"
        persistKey={null}
        syncStateToUrl={false}
        fixedFilters={[]}
        initialSortModel={initialSortModel}
        queryFn={queryFn}
        queryKey={queryKey}
        externalLoading={loading}
        columns={columns}
        getRowId={(row) => row.id}
        pageSizeOptions={[5, 10, 25, 50]}
        initialPageSize={5}
        noRowsText={getEmptyStateMessage('entity.noneFound', { entity: 'directory' })}
        disableColumnSorting
        disableColumnMenu
        interactionMode="static"
        autoHeight
        columnVisibilityModel={columnVisibilityModel}
        getRowHeight={() => TABLE_ROW_HEIGHT}
        sx={[
          ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
          {
            '& .MuiDataGrid-cell[data-field="directory"], & .MuiDataGrid-cell[data-field="lineCoverage"], & .MuiDataGrid-cell[data-field="functionCoverage"], & .MuiDataGrid-cell[data-field="branchCoverage"], & .MuiDataGrid-cell[data-field="statementCoverage"], & .MuiDataGrid-cell[data-field="details"], & .MuiDataGrid-cell[data-field="actions"]':
              {
                alignItems: 'flex-start',
              },
            '& .MuiDataGrid-columnHeaders': {
              minHeight: `${TABLE_GRID_HEADER_HEIGHT}px !important`,
            },
          },
        ]}
        gridProps={{
          onColumnVisibilityModelChange,
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'codeDirectory' })}
        description="This code directory entry will be removed from the coverage directory list."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'codeDirectory',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Code Directory"
        entityName={editTarget?.name ?? ''}
        projectId={projectId}
        tags={editTarget?.tags ?? []}
        loading={updateRowTagsColorPendingId === (editTarget?.id ?? null)}
        onClose={() => {
          if (updateRowTagsColorPendingId === (editTarget?.id ?? null)) return;
          setEditTarget(null);
        }}
        onSave={({ tags, colors }) => {
          if (!editTarget) return;
          onUpdateRowTagsColor({ row: editTarget, tags, colors });
          setEditTarget(null);
        }}
      />
    </>
  );
};
